import { Int64Value, StringValue } from "google-protobuf/google/protobuf/wrappers_pb";
import { ListEventsReq } from "proto/communities_pb";
import {
  AttendanceState,
  CancelEventReq,
  CreateEventReq,
  EventLocation,
  GetEventReq,
  InviteEventOrganizerReq,
  ListAllEventsReq,
  ListEventAttendeesReq,
  ListEventOrganizersReq,
  ListMyEventsReq,
  RemoveEventOrganizerReq,
  RequestCommunityInviteReq,
  SetEventAttendanceReq,
  UpdateEventReq,
} from "proto/events_pb";
import { Temporal } from "temporal-polyfill";

import client from "./client";

export async function listCommunityEvents(communityId: number, pageToken?: string, pageSize?: number) {
  const req = new ListEventsReq();
  req.setCommunityId(communityId);
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  if (pageSize) {
    req.setPageSize(pageSize);
  }

  const res = await client.communities.listEvents(req);
  return res.toObject();
}

export async function getEvent(eventId: number) {
  const req = new GetEventReq();
  req.setEventId(eventId);
  const res = await client.events.getEvent(req);
  return res.toObject();
}

export function cancelEvent(eventId: number) {
  const req = new CancelEventReq();
  req.setEventId(eventId);
  return client.events.cancelEvent(req);
}

export function RequestCommunityInvite(eventId: number) {
  const req = new RequestCommunityInviteReq();
  req.setEventId(eventId);
  return client.events.requestCommunityInvite(req);
}

interface ListEventUsersInput {
  eventId: number;
  pageSize?: number;
  pageToken?: string;
}

export async function listEventOrganizers({ eventId, pageSize, pageToken }: ListEventUsersInput) {
  const req = new ListEventOrganizersReq();
  req.setEventId(eventId);
  if (pageSize) {
    req.setPageSize(pageSize);
  }
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  const res = await client.events.listEventOrganizers(req);
  return res.toObject();
}

export async function listEventAttendees({ eventId, pageSize, pageToken }: ListEventUsersInput) {
  const req = new ListEventAttendeesReq();
  req.setEventId(eventId);
  if (pageSize) {
    req.setPageSize(pageSize);
  }
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  const res = await client.events.listEventAttendees(req);
  return res.toObject();
}

export async function setEventAttendance({
  attendanceState,
  eventId,
}: {
  attendanceState: AttendanceState;
  eventId: number;
}) {
  const req = new SetEventAttendanceReq();
  req.setEventId(eventId);
  req.setAttendanceState(attendanceState);
  const res = await client.events.setEventAttendance(req);
  return res.toObject();
}

interface EventInput {
  content: string;
  photoKey?: string;
  title: string;
  startTime: Temporal.PlainDateTime;
  endTime: Temporal.PlainDateTime;
  address: string;
  lat: number;
  lng: number;
  parentCommunityId?: number;
}

export type CreateEventInput = EventInput;

export async function createEvent(input: CreateEventInput) {
  const req = new CreateEventReq();
  req.setTitle(input.title);
  req.setContent(input.content);
  req.setStartDatetimeIso8601Local(input.startTime.toString());
  req.setEndDatetimeIso8601Local(input.endTime.toString());

  if (input.photoKey) {
    req.setPhotoKey(input.photoKey);
  }

  const location = new EventLocation();
  location.setAddress(input.address);
  location.setLat(input.lat);
  location.setLng(input.lng);
  req.setLocation(location);

  if (input.parentCommunityId) {
    req.setParentCommunityId(input.parentCommunityId);
  }

  const res = await client.events.createEvent(req);
  return res.toObject();
}

export interface UpdateEventInput extends Partial<Omit<EventInput, "parentCommunityId">> {
  eventId: number;
  shouldNotify: boolean;
}

export async function updateEvent(input: UpdateEventInput) {
  const req = new UpdateEventReq();
  req.setEventId(input.eventId);
  if (input.title) {
    req.setTitle(new StringValue().setValue(input.title));
  }
  if (input.content) {
    req.setContent(new StringValue().setValue(input.content));
  }
  if (input.startTime) {
    req.setStartDatetimeIso8601Local(new StringValue().setValue(input.startTime.toString()));
  }
  if (input.endTime) {
    req.setEndDatetimeIso8601Local(new StringValue().setValue(input.endTime.toString()));
  }

  if (input.photoKey) {
    req.setPhotoKey(new StringValue().setValue(input.photoKey));
  }

  if (input.address && input.lat && input.lng) {
    const location = new EventLocation();
    location.setAddress(input.address);
    location.setLat(input.lat);
    location.setLng(input.lng);
    req.setLocation(location);
  }

  if (input.shouldNotify) {
    req.setShouldNotify(input.shouldNotify);
  }

  const res = await client.events.updateEvent(req);
  return res.toObject();
}

export interface ListAllEventsInput {
  pastEvents: boolean;
  pageSize?: number;
  pageToken?: string;
  showCancelled?: boolean;
}

export async function listAllEvents({ pastEvents = false, pageSize, pageToken, showCancelled }: ListAllEventsInput) {
  const req = new ListAllEventsReq();

  if (pastEvents !== undefined) {
    req.setPast(pastEvents);
  }
  if (pageSize) {
    req.setPageSize(pageSize);
  }
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  if (showCancelled !== undefined) {
    req.setIncludeCancelled(showCancelled);
  }

  const res = await client.events.listAllEvents(req);
  return res.toObject();
}

export interface ListMyEventsInput {
  excludeAttending?: boolean;
  myCommunities?: boolean;
  myCommunitiesExcludeGlobal?: boolean;
  pageNumber?: number;
  pageSize?: number;
  pageToken?: string;
  pastEvents?: boolean;
  showCancelled?: boolean;
}

export async function listMyEvents({
  excludeAttending,
  myCommunities,
  myCommunitiesExcludeGlobal,
  pageNumber,
  pageSize,
  pageToken,
  pastEvents,
  showCancelled,
}: ListMyEventsInput) {
  const req = new ListMyEventsReq();

  if (excludeAttending) {
    req.setExcludeAttending(true);
  } else {
    req.setAttending(true);
    req.setOrganizing(true);
  }

  if (myCommunities !== undefined) {
    req.setMyCommunities(myCommunities);
  }

  if (myCommunitiesExcludeGlobal !== undefined) {
    req.setMyCommunitiesExcludeGlobal(myCommunitiesExcludeGlobal);
  }

  if (pastEvents !== undefined) {
    req.setPast(pastEvents);
  }
  if (pageNumber) {
    req.setPageNumber(pageNumber);
  }
  if (pageSize) {
    req.setPageSize(pageSize);
  }
  if (pageToken) {
    req.setPageToken(pageToken);
  }
  if (showCancelled !== undefined) {
    req.setIncludeCancelled(showCancelled);
  }

  const res = await client.events.listMyEvents(req);
  return res.toObject();
}

export async function inviteEventOrganizer(eventId: number, userId: number) {
  const req = new InviteEventOrganizerReq();
  req.setEventId(eventId);
  req.setUserId(userId);
  const res = await client.events.inviteEventOrganizer(req);
  return res.toObject();
}

export async function removeEventOrganizer(eventId: number, userId: number) {
  const req = new RemoveEventOrganizerReq();
  req.setEventId(eventId);
  req.setUserId(new Int64Value().setValue(userId));
  const res = await client.events.removeEventOrganizer(req);
  return res.toObject();
}
